import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Hinzufuegen und Entfernen laufen bewusst ueber unterschiedliche
 * Endpunkte, mit unterschiedlicher Nachbehandlung:
 *
 * - addGift: /cart/add.js - einzig dieser Endpunkt kann ein Produkt
 *   hinzufuegen, das noch NICHT im Warenkorb liegt. Die Antwort enthaelt
 *   aber nur das neue Item, nicht den vollstaendigen Warenkorb - deshalb
 *   wird danach separat frisch /cart.js abgerufen UND durch
 *   createCartFromAjaxResponse() umgewandelt, bevor es an andere
 *   Theme-Komponenten weitergereicht wird.
 *
 * - removeGift: /cart/change.js - hier existiert die Zeile bereits, die
 *   Antwort ist bereits der vollstaendige Warenkorb.
 *
 * WICHTIG - warum wir NICHT mehr detail.sections vom Event vertrauen:
 * Diagnose hat gezeigt, dass zwei fast zeitgleiche Update-Events fuer
 * denselben Warenkorb-Zustand widerspruechliche "reached"-Werte mitliefern
 * koennen (vermutlich weil das Theme das Event ueber zwei parallele Pfade
 * feuert, von denen einer eine nicht ganz synchrone Zwischen-Momentaufnahme
 * mitgibt). Das fuehrte dazu, dass ein gerade erst hinzugefuegtes Geschenk
 * Sekundenbruchteile spaeter faelschlich wieder entfernt wurde.
 *
 * Deshalb holt handleMutation() jetzt bei JEDER Auswertung aktiv eine
 * garantiert frische Section-Momentaufnahme direkt vom Server (statt der
 * moeglicherweise veralteten detail.sections aus dem Event), bevor eine
 * Add/Remove-Entscheidung getroffen wird. Kostet einen zusaetzlichen
 * Request pro Auswertung, eliminiert dafuer das Wettrennen zuverlaessig.
 *
 * Feedback-Loop-Schutz (suppressUntil): verhindert zusaetzlich, dass
 * unsere eigenen addGift/removeGift-Aktionen sich selbst erneut ausloesen.
 */

let busy = false;
let suppressUntil = 0;

function suppressBriefly() {
  suppressUntil = Date.now() + 2000;
}

function getConfig() {
  const el = document.getElementById('invisiwear-gwp-config');
  if (!el?.textContent) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (error) {
    console.warn('[invisiwear-cart-rewards] Konnte Konfiguration nicht parsen:', error);
    return null;
  }
}

function extractConfigFromSections(sections) {
  if (!sections) return null;
  for (const html of Object.values(sections)) {
    if (typeof html !== 'string' || !html.includes('invisiwear-gwp-config')) continue;
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const el = doc.getElementById('invisiwear-gwp-config');
      if (el?.textContent) return JSON.parse(el.textContent);
    } catch (error) {
      console.warn('[invisiwear-cart-rewards] Konnte Konfiguration aus Section nicht parsen:', error);
    }
  }
  return null;
}

function sectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

async function fetchCart() {
  const response = await fetch('/cart.js', {
    headers: { Accept: 'application/json', 'X-GWP-Internal': '1' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Cart-Abruf fehlgeschlagen: ${response.status}`);
  return response.json();
}

// Holt eine garantiert frische Section-Momentaufnahme direkt vom Server,
// unabhaengig davon was das ausloesende Event mitgeliefert hat.
async function fetchFreshConfig() {
  const sections = sectionIds();
  try {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-GWP-Internal': '1' },
      credentials: 'same-origin',
      body: JSON.stringify({
        updates: {},
        sections: sections.join(','),
        sections_url: window.location.pathname,
      }),
    });
    if (!response.ok) throw new Error(`Frischer Abruf fehlgeschlagen: ${response.status}`);
    const parsed = await response.json();
    const config = extractConfigFromSections(parsed.sections);
    return config || getConfig();
  } catch (error) {
    console.warn('[invisiwear-cart-rewards] Frischer Abruf fehlgeschlagen, nutze DOM-Stand als Fallback:', error);
    return getConfig();
  }
}

async function submitCartChange(action, url, body, sections, needsFreshCartForConversion) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'cart',
      lines: [],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(url, fetchConfig('json', { body, headers: { 'X-GWP-Internal': '1' } }));
    const parsed = await response.json();

    if (parsed.errors) {
      console.warn('[invisiwear-cart-rewards] Warenkorb-Update mit Fehlern:', parsed.errors);
      deferred.reject(new Error(JSON.stringify(parsed.errors)));
      return;
    }

    for (const sectionId of sections) {
      if (parsed.sections?.[sectionId]) {
        morphSection(sectionId, parsed.sections[sectionId], { mode: 'hydration' });
      }
    }

    let resolvedCart;
    try {
      const cartShapedJson = needsFreshCartForConversion ? await fetchCart() : parsed;
      resolvedCart = CartLinesUpdateEvent.createCartFromAjaxResponse(cartShapedJson);
    } catch (conversionError) {
      console.warn('[invisiwear-cart-rewards] Cart-Konvertierung fehlgeschlagen:', conversionError);
      deferred.reject(conversionError);
      return;
    }

    deferred.resolve({
      cart: resolvedCart,
      detail: { sections: parsed.sections, source: 'invisiwear-cart-rewards', didError: false },
    });
  } catch (error) {
    deferred.reject(error);
    console.warn('[invisiwear-cart-rewards] Warenkorb-Update fehlgeschlagen:', error);
  }
}

async function addGift(key, milestone) {
  const sections = sectionIds();
  const body = JSON.stringify({
    id: milestone.giftVariantId,
    quantity: 1,
    properties: { _gwp_gift: key, _gwp_source: 'auto' },
    sections: sections.join(','),
    sections_url: window.location.pathname,
  });

  const url = window.Theme?.routes?.cart_add_url || '/cart/add.js';
  await submitCartChange('add', url, body, sections, true);
}

async function removeGift(key) {
  const sections = sectionIds();
  const body = JSON.stringify({
    id: key,
    quantity: 0,
    sections: sections.join(','),
    sections_url: window.location.pathname,
  });

  const url = window.Theme?.routes?.cart_change_url || '/cart/change.js';
  await submitCartChange('remove', url, body, sections, false);
}

async function evaluateMilestone(key, milestone) {
  if (!milestone.enabled) return;

  if (!milestone.reached && milestone.giftPresent && milestone.giftSourceAuto) {
    suppressBriefly();
    await removeGift(milestone.giftKey);
    return;
  }

  if (milestone.mode !== 'auto' || !milestone.autoAdd) return;
  if (!milestone.giftVariantId) return;

  if (milestone.reached && !milestone.giftPresent) {
    suppressBriefly();
    await addGift(key, milestone);
  }
}

async function runMilestones(config) {
  if (!config?.milestones) return;
  for (const [key, milestone] of Object.entries(config.milestones)) {
    await evaluateMilestone(key, milestone);
  }
}

async function handleMutation() {
  if (busy) return;
  busy = true;
  try {
    const config = await fetchFreshConfig();
    await runMilestones(config);
  } catch (error) {
    console.warn('[invisiwear-cart-rewards] Auswertung fehlgeschlagen:', error);
  } finally {
    busy = false;
  }
}

// Primaerer Trigger: Theme-eigenes Event, wenn vorhanden.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.target === document) return;
  if (Date.now() < suppressUntil) return;

  event.promise
    ?.then(() => handleMutation())
    .catch((error) => {
      if (error?.name !== 'AbortError') {
        console.warn('[invisiwear-cart-rewards] Event promise rejected:', error);
      }
    });
});

/**
 * Zusaetzlicher, zuverlaessigerer Trigger direkt auf Netzwerk-Ebene:
 * Diagnose hat gezeigt, dass das Theme-eigene cartLinesUpdate-Event nicht
 * konsistent bei jeder Warenkorb-Aenderung ankommt (Ursache im Theme nicht
 * abschliessend geklaert). Jede echte Warenkorb-Aenderung muss aber
 * zwangslaeufig ueber /cart/add.js, /cart/change.js, /cart/update.js oder
 * /cart/clear.js laufen - das faengt dieser Fetch-Hook zuverlaessig ab,
 * unabhaengig vom Theme-Event. suppressUntil verhindert weiterhin, dass
 * unsere eigenen addGift/removeGift-Aufrufe sich selbst erneut ausloesen.
 */
const originalFetch = window.fetch;
window.fetch = function (...args) {
  const requestUrl = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
  const isOwnRequest = args[1]?.headers?.['X-GWP-Internal'] === '1';
  const isCartMutation = !isOwnRequest && /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(requestUrl);
  const result = originalFetch.apply(this, args);

  if (isCartMutation) {
    result
      .then((response) => {
        if (!response.ok) return;
        if (Date.now() < suppressUntil) return;
        suppressBriefly();
        setTimeout(() => handleMutation(), 150);
      })
      .catch(() => {});
  }

  return result;
};

(async () => {
  if (busy) return;
  busy = true;
  try {
    await runMilestones(getConfig());
  } finally {
    busy = false;
  }
})();